/**
 * Client-side Profile Resolver and LRU Cache.
 *
 * Resolves profiles (name, avatar, metadata) for visible authors/members
 * via local cache and debounced batch network requests against the Auth node.
 */

import { getSessionToken } from './index';

export interface ProfileSummary {
  id: string;
  accountId?: string;
  profileType: string;
  name: string;
  verificationLevel?: number;
  visibility?: string;
  avatar?: string;
  metadata?: Record<string, unknown>;
}

interface CacheEntry {
  profile: ProfileSummary | null;
  expiresAt: number;
}

const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
const NEGATIVE_CACHE_TTL_MS = 60 * 1000; // 1 minute for missing profiles
const MAX_CACHE_ENTRIES = 1000;

const profileCache = new Map<string, CacheEntry>();
const pendingBatchIds = new Set<string>();
let batchTimer: ReturnType<typeof setTimeout> | null = null;
const batchResolvers = new Map<string, Array<(profile: ProfileSummary | null) => void>>();

const AUTH_API_URL: string =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_AUTH_API_URL) ??
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) ??
  'http://localhost:3001';

/**
 * Prime cache with known profiles (e.g. from workspace member lists or local profiles).
 */
export function primeProfileCache(profiles: ProfileSummary[]): void {
  const now = Date.now();
  for (const profile of profiles) {
    if (!profile || !profile.id) continue;
    profileCache.set(profile.id, {
      profile,
      expiresAt: now + CACHE_TTL_MS,
    });
  }
}

/**
 * Check if a profile ID is cached (either positive or negative cache) and unexpired.
 */
export function isProfileCached(profileId: string): boolean {
  if (!profileId) return false;
  const entry = profileCache.get(profileId);
  return Boolean(entry && entry.expiresAt > Date.now());
}

/**
 * Synchronously get cached profile if available and not expired.
 * Also checks active local account profiles in localStorage as a synchronous fallback.
 */
export function getProfileSummarySync(profileId: string): ProfileSummary | null {
  if (!profileId) return null;

  const entry = profileCache.get(profileId);
  if (entry && entry.expiresAt > Date.now()) {
    return entry.profile;
  }

  // Check local storage profiles
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:profiles') : null;
    if (raw) {
      const localProfiles = JSON.parse(raw);
      const found = localProfiles.find((p: any) => p.id === profileId);
      if (found) {
        const summary: ProfileSummary = {
          id: found.id,
          accountId: found.accountId,
          profileType: found.profileType,
          name: found.name,
          verificationLevel: found.verificationLevel,
          visibility: found.visibility,
          avatar: found.metadata?.avatar,
          metadata: found.metadata,
        };
        profileCache.set(profileId, { profile: summary, expiresAt: Date.now() + CACHE_TTL_MS });
        return summary;
      }
    }
  } catch {
    // Ignore parse error
  }

  return null;
}

/**
 * Queue a list of profile IDs for background batch resolution.
 */
export function queueProfileResolution(profileIds: string[]): void {
  const missing = profileIds.filter((id) => id && !isProfileCached(id) && !getProfileSummarySync(id));
  if (missing.length === 0) return;

  for (const id of missing) {
    pendingBatchIds.add(id);
  }

  if (!batchTimer) {
    batchTimer = setTimeout(() => {
      batchTimer = null;
      flushBatch();
    }, 30);
  }
}

/**
 * Resolve a single profile by ID, batching if not cached.
 */
export async function resolveProfile(profileId: string): Promise<ProfileSummary | null> {
  const entry = profileCache.get(profileId);
  if (entry && entry.expiresAt > Date.now()) {
    return entry.profile;
  }

  const cached = getProfileSummarySync(profileId);
  if (cached) return cached;

  // If already negative-cached and unexpired, return null without re-fetching
  if (entry && entry.expiresAt > Date.now()) {
    return null;
  }

  return new Promise((resolve) => {
    const callbacks = batchResolvers.get(profileId) || [];
    callbacks.push(resolve);
    batchResolvers.set(profileId, callbacks);

    pendingBatchIds.add(profileId);

    if (!batchTimer) {
      batchTimer = setTimeout(() => {
        batchTimer = null;
        flushBatch();
      }, 30);
    }
  });
}

/**
 * Resolve multiple profiles by IDs.
 */
export async function resolveProfiles(profileIds: string[]): Promise<Map<string, ProfileSummary>> {
  const results = new Map<string, ProfileSummary>();
  const toFetch: string[] = [];

  for (const id of profileIds) {
    if (!id) continue;
    const entry = profileCache.get(id);
    if (entry && entry.expiresAt > Date.now()) {
      if (entry.profile) results.set(id, entry.profile);
      continue;
    }
    const cached = getProfileSummarySync(id);
    if (cached) {
      results.set(id, cached);
    } else {
      toFetch.push(id);
    }
  }

  if (toFetch.length === 0) {
    return results;
  }

  await Promise.all(
    toFetch.map(async (id) => {
      const p = await resolveProfile(id);
      if (p) results.set(id, p);
    })
  );

  return results;
}

async function flushBatch(): Promise<void> {
  if (pendingBatchIds.size === 0) return;

  const ids = Array.from(pendingBatchIds).slice(0, 100);
  for (const id of ids) {
    pendingBatchIds.delete(id);
  }

  const token = await getSessionToken();
  const now = Date.now();

  try {
    let fetchedProfiles: Array<{
      id: string;
      account_id?: string;
      profile_type: string;
      name: string;
      verification_level?: number;
      visibility?: string;
      metadata?: Record<string, unknown>;
    }> = [];

    if (token) {
      const response = await fetch(`${AUTH_API_URL}/api/profiles/batch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids }),
      });

      if (response.ok) {
        const json = await response.json();
        fetchedProfiles = json.profiles || [];
      }
    }

    const fetchedMap = new Map<string, ProfileSummary>();
    for (const p of fetchedProfiles) {
      const summary: ProfileSummary = {
        id: p.id,
        accountId: p.account_id,
        profileType: p.profile_type,
        name: p.name,
        verificationLevel: p.verification_level,
        visibility: p.visibility,
        avatar: (p.metadata?.avatar as string | undefined) ?? undefined,
        metadata: p.metadata,
      };
      fetchedMap.set(p.id, summary);
      profileCache.set(p.id, {
        profile: summary,
        expiresAt: now + CACHE_TTL_MS,
      });
    }

    // Handle missing IDs (negative cache to avoid pounding endpoint)
    for (const id of ids) {
      if (!fetchedMap.has(id)) {
        profileCache.set(id, {
          profile: null,
          expiresAt: now + NEGATIVE_CACHE_TTL_MS,
        });
      }
    }

    // Prune LRU cache if oversized
    if (profileCache.size > MAX_CACHE_ENTRIES) {
      const entriesToPrune = profileCache.size - MAX_CACHE_ENTRIES;
      const keys = Array.from(profileCache.keys()).slice(0, entriesToPrune);
      for (const k of keys) profileCache.delete(k);
    }

    // Resolve promises
    for (const id of ids) {
      const callbacks = batchResolvers.get(id);
      if (callbacks) {
        const resolved = fetchedMap.get(id) || null;
        callbacks.forEach((cb) => cb(resolved));
        batchResolvers.delete(id);
      }
    }

    // Dispatch global event for reactive UI updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('yada:profiles:resolved', { detail: { ids } }));
    }
  } catch (err) {
    console.warn('[profileResolver] Batch fetch failed:', err);
    // Resolve pending callbacks with null on error
    for (const id of ids) {
      const callbacks = batchResolvers.get(id);
      if (callbacks) {
        callbacks.forEach((cb) => cb(null));
        batchResolvers.delete(id);
      }
    }
  }

  // If there are still pending IDs left, schedule another batch
  if (pendingBatchIds.size > 0 && !batchTimer) {
    batchTimer = setTimeout(() => {
      batchTimer = null;
      flushBatch();
    }, 30);
  }
}
